import * as Sentry from '@sentry/react';
import MainLayout from 'layouts/MainLayout';
import CSAPlusLayout from 'layouts/CSAPlusLayout';
import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';
import CreateNew from './pages/project-management/create-project/CreateNew';
import ProjectListView from 'pages/project-management/project-list/ProjectListView';
import ProjectDetails from './pages/project-management/project-details/ProjectDetails';
import MainLayoutProvider from 'providers/MainLayoutProvider';
import Error404 from 'pages/error/Error404';
import Error403 from 'pages/error/Error403';
import Error500 from 'pages/error/Error500';
import SimpleSignIn from 'pages/authentication/SignIn';
import SimpleSignUp from 'pages/authentication/SignUp';
import AccountCreateStatus from 'pages/authentication/AccountCreateStatus';
import SimpleResetPassword from 'pages/authentication/ResetPassword';
import SimpleLockScreen from 'pages/authentication/LockScreen';
import SimpleTwoFA from 'pages/authentication/TwoFA';
import SimpleForgotPassword from 'pages/authentication/ForgotPassword';
import Showcase from 'pages/Showcase';

import App from 'App';
import Crm from 'pages/dashboard/Crm';
import TravelAgency from 'pages/dashboard/TravelAgency';
import UpdateProject from './pages/project-management/create-project/UpdateProject';
import ChatConversation from './pages/apps/chat/ChatConversation';
import ChatConversationLifeSciences from './pages/apps/ethico-lifesciences/ChatConversation-LifeSciences';
import ProjectDetailsDimensions from 'pages/project-management/project-details/ProjectDetailsDimensions';
import ProjectDetailsWithTabs from 'pages/project-management/project-details/ProjectDetailsWithTabs';
import AIAssurancePage from 'pages/project-management/ai-assurance/AIAssurancePage';
import RealtimeMonitoringPage from 'pages/project-management/realtime-monitoring/RealtimeMonitoringPage';
import AddUser from 'pages/users/AddUser';
import ViewAllUsers from 'pages/users/ViewAll';
import ViewAllDivisions from 'pages/divisions/ViewAllDivisions';
import CreateDivision from 'pages/divisions/CreateDivision';
import RoleGuard from 'components/common/RoleGuard';
import { CSAPlusIndex } from 'pages/csa-plus';
import CSAPlusCreateProject from 'pages/csa-plus/CreateProject';
import CSAPlusProjectListView from 'pages/csa-plus/ProjectListView';
import CSAPlusProjectDetails from 'pages/csa-plus/ProjectDetails';
import CSADetailsTabsRedirect from 'pages/csa-plus/CSADetailsTabsRedirect';
import ProductSelection from 'pages/common/product-selection/ProductSelection';
import MTCPlusCreateProject from 'pages/mtc_plus/CreateProject';
import MTCPlusAllProjects from 'pages/mtc_plus/all-projects';
import MTCPlusProjectDetails from 'pages/mtc_plus/ProjectDetails';
import MTCDetailsTabsRedirect from 'pages/mtc_plus/MTCDetailsTabsRedirect';
import ATEPlusCreateProject from 'pages/ate_plus/CreateProject';
import ATEPlusAllProjects from 'pages/ate_plus/AllProjects';
import ATEPlusProjectDetails from 'pages/ate_plus/ProjectDetails';
import ATEDetailsTabsRedirect, { ATEPlusProjectLegacyRedirect } from 'pages/ate_plus/ATEDetailsTabsRedirect';

/** Redirects /project to all-projects; prevents Ecommerce from showing on project routes */
const ProjectIndexRedirect = () => {
  const userId = localStorage.getItem('userId') || '';
  const target = userId ? `/project/${userId}/all-projects` : '/';
  return <Navigate to={target} replace />;
};

const routes: RouteObject[] = [
  {
    element: <App />,
    children: [
      {
        path: '/project',
        element: (
          <MainLayoutProvider>
            <MainLayout />
          </MainLayoutProvider>
        ),
        children: [
          {
            index: true,
            element: <ProjectIndexRedirect />
          },
          {
            path: '/project/:userId',
            children: [
              {
                path: 'dashboard',
                element: <Crm />
              },
              {
                path: 'create-project',
                element: <CreateNew />
              },
              {
                path: 'all-projects',
                element: <ProjectListView />
              },
              {
                path: 'crm',
                element: <Crm />
              },
              {
                path: 'travel-agency',
                element: <TravelAgency />
              }
            ]
          },
          {
            path: '/project',
            children: [
              {
                path: 'details',
                element: <ProjectDetails />
              },
              {
                path: 'details-tabs',
                element: <ProjectDetailsWithTabs />
              },
              {
                path: 'dimensions',
                element: <ProjectDetailsDimensions />
              },
              {
                path: 'dashboard',
                element: <Crm />
              },
              {
                path: 'update-project',
                element: <UpdateProject />
              },
              {
                path: 'ai-assurance',
                element: <AIAssurancePage />
              },
              {
                path: 'realtime-monitoring',
                element: <RealtimeMonitoringPage />
              }
            ]
          }
        ]
      },
      {
        path: '/users',
        element: (
          <MainLayoutProvider>
            <MainLayout />
          </MainLayoutProvider>
        ),
        children: [
          {
            path: 'add',
            element: (
              <RoleGuard allowedRoles={['admin', 'manager', 'owner']}>
                <AddUser />
              </RoleGuard>
            )
          },
          {
            path: 'view-all',
            element: (
              <RoleGuard allowedRoles={['admin', 'manager', 'owner']}>
                <ViewAllUsers />
              </RoleGuard>
            )
          }
        ]
      },
      {
        path: '/divisions',
        element: (
          <MainLayoutProvider>
            <MainLayout />
          </MainLayoutProvider>
        ),
        children: [
          {
            path: 'view-all',
            element: (
              <RoleGuard allowedRoles={['admin', 'manager', 'owner']}>
                <ViewAllDivisions />
              </RoleGuard>
            )
          },
          {
            path: 'create',
            element: (
              <RoleGuard allowedRoles={['admin', 'manager', 'owner']}>
                <CreateDivision />
              </RoleGuard>
            )
          }
        ]
      },
      {
        path: '/product-selection',
        element: <ProductSelection />
      },
      {
        path: '/csa+',
        element: (
          <MainLayoutProvider>
            <CSAPlusLayout />
          </MainLayoutProvider>
        ),
        children: [
          {
            index: true,
            element: <CSAPlusIndex />
          },
          {
            path: 'create-project',
            element: <CSAPlusCreateProject />
          },
          {
            path: 'all-projects',
            element: <CSAPlusProjectListView />
          },
          {
            path: 'details-tabs',
            element: <CSADetailsTabsRedirect />
          },
          {
            path: 'project-details/:projectId',
            element: <CSAPlusProjectDetails />
          },
          {
            path: 'ai-assurance',
            element: <AIAssurancePage />
          },
          {
            path: 'realtime-monitoring',
            element: <RealtimeMonitoringPage />
          },
          {
            path: 'update-project/:projectId',
            element: <UpdateProject />
          }
        ]
      },
      {
        path: '/mtc+',
        element: (
          <MainLayoutProvider>
            <MainLayout />
          </MainLayoutProvider>
        ),
        children: [
          {
            path: 'create-project',
            element: <MTCPlusCreateProject />
          },
          {
            path: 'all-projects',
            element: <MTCPlusAllProjects />
          },
          {
            path: 'details-tabs',
            element: <MTCDetailsTabsRedirect />
          },
          {
            path: 'project-details/:projectId',
            element: <MTCPlusProjectDetails />
          },
          {
            path: 'ai-assurance',
            element: <AIAssurancePage />
          },
          {
            path: 'realtime-monitoring',
            element: <RealtimeMonitoringPage />
          },
          {
            path: 'update-project/:projectId',
            element: <UpdateProject />
          }
        ]
      },
      {
        path: '/ate+',
        element: (
          <MainLayoutProvider>
            <MainLayout />
          </MainLayoutProvider>
        ),
        children: [
          {
            path: 'create-project',
            element: <ATEPlusCreateProject />
          },
          {
            path: 'all-projects',
            element: <ATEPlusAllProjects />
          },
          {
            path: 'details-tabs',
            element: <ATEDetailsTabsRedirect />
          },
          {
            path: 'project/:projectId',
            element: <ATEPlusProjectDetails />
          },
          {
            path: 'project-details/:projectId',
            element: <ATEPlusProjectLegacyRedirect />
          },
          {
            path: 'ai-assurance',
            element: <AIAssurancePage />
          },
          {
            path: 'realtime-monitoring',
            element: <RealtimeMonitoringPage />
          },
          {
            path: 'update-project/:projectId',
            element: <UpdateProject />
          }
        ]
      },
      {
        path: '/ethico',
        element: (
          <MainLayoutProvider>
            <MainLayout />
          </MainLayoutProvider>
        ),
        children: [
          {
            index: true,
            element: <ChatConversation />
          },
          {
            path: 'ethico-lifesciences',
            element: <ChatConversationLifeSciences />
          }
        ]
      },
      {
        path: '/pages/errors/',
        children: [
          {
            path: '404',
            element: <Error404 />
          },
          {
            path: '403',
            element: <Error403 />
          },
          {
            path: '500',
            element: <Error500 />
          }
        ]
      },
      {
        path: '/',

        children: [
          {
            index: true,
            element: <SimpleSignIn />
          },
          {
            path: 'sign-up',
            element: <SimpleSignUp />
          },
          {
            path: 'account-create-status',
            element: <AccountCreateStatus />
          },
          {
            path: 'forgot-password',
            element: <SimpleForgotPassword />
          },
          {
            path: 'reset-password',
            element: <SimpleResetPassword />
          },
          {
            path: 'lock-screen',
            element: <SimpleLockScreen />
          },
          {
            path: '2FA',
            element: <SimpleTwoFA />
          }
        ]
      },

      {
        path: 'showcase',
        element: <Showcase />
      },
      {
        path: '*',
        element: <Error404 />
      }
    ]
  }
];

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);
export const router = sentryCreateBrowserRouter(routes);

export default routes;
